<script setup lang="ts">
import { computed } from 'vue';
import { NDrawer, NDrawerContent, NTag } from 'naive-ui';
import type {
  AnnotationInstance,
  FieldCatalogEntity,
  FieldCatalogFilter,
  TemplateFieldType,
} from '@/api';
import type { AnnotationDraft } from '@/composables/contractGeneration/useTemplateAnnotations';
import AnnotationConfigForm from './AnnotationConfigForm.vue';
import { annotationConfigStyleVars, annotationConfigTitle } from './annotationConfig';

/**
 * The tag-settings form as a right-hand drawer — the shape the Word add-in
 * and the snippet library use. The form itself lives in
 * `AnnotationConfigForm`; this only supplies the sheet, its header, and the
 * open/close plumbing. Apply and Remove close the drawer after emitting.
 */
interface Props {
  show: boolean;
  /**
   * Anchor element for the highlighted chip. Kept for backwards compatibility
   * with the old popover-style API; the drawer doesn't need it.
   */
  reference: HTMLElement | null;
  annotation: AnnotationDraft | null;
  filterCatalog: FieldCatalogFilter[];
  /** Type of the field being configured — drives filter eligibility. */
  fieldType?: TemplateFieldType | null;
  /** Existing entity/object aliases — feed into the alias dropdown. */
  instances?: AnnotationInstance[];
  /** Catalog of entities — populates the sub-field picker for entity bindings. */
  entityCatalog?: FieldCatalogEntity[];
  /** A synthetic draft to preview and copy, not a chip in the document. */
  previewOnly?: boolean;
  /** Show the "Don't show in document" switch (Word add-in). */
  showHidden?: boolean;
  /** Initial value of that switch when the drawer opens. */
  initialHidden?: boolean;
}

interface Emits {
  (e: 'update:show', value: boolean): void;
  /** `options.hidden` is set when the "Don't show in document" switch is on. */
  (e: 'apply', patch: Partial<AnnotationDraft>, options?: { hidden?: boolean }): void;
  (e: 'remove'): void;
}

const props = withDefaults(defineProps<Props>(), {
  fieldType: null,
  instances: () => [],
  entityCatalog: () => [],
  previewOnly: false,
  showHidden: false,
  initialHidden: false,
});
const emit = defineEmits<Emits>();

const title = computed<string>(() => annotationConfigTitle(props.annotation));

const drawerWidth = computed<number>(() => {
  if (typeof window === 'undefined') return 560;
  // Wider drawer makes the filter cards (with arg labels, defaults, examples)
  // breathe — at 360px everything had to truncate. Capped at the viewport so
  // narrow hosts (the Word task pane) get a full-width sheet instead of an
  // overflowing panel.
  return Math.min(620, Math.max(420, Math.round(window.innerWidth * 0.4)), window.innerWidth);
});

function close(): void {
  emit('update:show', false);
}

function handleApply(patch: Partial<AnnotationDraft>, options?: { hidden?: boolean }): void {
  emit('apply', patch, options);
  close();
}

function handleRemove(): void {
  emit('remove');
  close();
}
</script>

<template>
  <n-drawer
    :show="show"
    :width="drawerWidth"
    placement="right"
    :mask-closable="true"
    :auto-focus="false"
    class="config-drawer"
    @update:show="(value: boolean) => emit('update:show', value)"
  >
    <!-- Inline custom properties rather than v-bind(): the drawer teleports
         to <body>, so component-root CSS vars never reach it. -->
    <n-drawer-content
      closable
      :native-scrollbar="false"
      :body-content-style="{ padding: '0' }"
      :style="annotationConfigStyleVars"
    >
      <template #header>
        <span class="config-drawer__title">
          {{ title }}
          <n-tag
            v-if="previewOnly"
            size="small"
            round
            :bordered="false"
            type="info"
            class="config-drawer__origin-tag"
          >
            Preview
          </n-tag>
          <n-tag
            v-else-if="annotation?.origin === 'extracted'"
            size="small"
            round
            :bordered="false"
            type="warning"
            class="config-drawer__origin-tag"
          >
            From source
          </n-tag>
        </span>
      </template>

      <annotation-config-form
        :active="show"
        :annotation="annotation"
        :filter-catalog="filterCatalog"
        :field-type="fieldType"
        :instances="instances"
        :entity-catalog="entityCatalog"
        :preview-only="previewOnly"
        :show-hidden="showHidden"
        :initial-hidden="initialHidden"
        @apply="handleApply"
        @remove="handleRemove"
        @cancel="close"
      />
    </n-drawer-content>
  </n-drawer>
</template>

<style scoped>
.config-drawer__title {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-weight: var(--tplcfg-fw-semibold);
}

.config-drawer__origin-tag {
  font-size: var(--tplcfg-fs-nano) !important;
  font-weight: var(--tplcfg-fw-semibold);
  letter-spacing: var(--tplcfg-track-caps);
}
</style>
